'use client';

import type { Offer } from '@repo/contracts/marketplace/offers/types';
import { css } from '@repo/pandacss/css';
import { AnalyticsSkuViewed } from '@repo/platform-analytics/AnalyticsSkuViewed';
import { getOrCreateId } from '@repo/utils/static-ids';
import Image from 'next/image';
import { useRef } from 'react';
import { useBranchId, useFunnelStepId } from '@/features/funnel/state/funnel/Context/hooks';
import { usePageActionForBranch } from '@/features/shared/analytics/hooks/usePageActionForBranch';
type Props = {
  offer: Offer;
  rank: number;
  onOpenDetails: (offer: Offer) => void;
};
export function Card({
  rank,
  offer,
  onOpenDetails
}: Props) {
  const activityIdRef = useRef('');
  const activityId = getOrCreateId(activityIdRef);
  const stepId = useFunnelStepId();
  const branchId = useBranchId();
  const firePageAction = usePageActionForBranch({
    actionSection: 'early_recommendations_sidebar'
  });
  const handleClick = (offer_0: Offer) => {
    firePageAction({
      action_name: 'partner_modal_opened',
      action_input: offer_0.id,
      action_type: 'select',
      activity_id: activityId
    });
    onOpenDetails(offer_0);
  };
  return <AnalyticsSkuViewed id={offer.id} name={offer.displayName} monetizable="no" impressionSection="early_recommendations_side_bar" impressionPosition={rank - 1} additionalAttributes={{
    page_branch_name: branchId,
    page_name: stepId
  }}>
      <article aria-label={offer.displayName} className={css({
      position: 'relative',
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1',
      height: {
        base: '[100px]',
        sm: '[120px]'
      },
      minWidth: {
        base: '[100px]',
        sm: '[150px]'
      },
      maxWidth: {
        base: '[150px]',
        sm: '[100%]'
      }
    })}>
        <button type="button" onClick={() => handleClick(offer)} aria-label={`View details for ${offer.displayName}`} className={css({
        cursor: 'pointer',
        flex: '1',
        bg: 'common.white',
        width: '[100%]',
        rounded: 'md',
        border: '1px solid',
        borderColor: 'neutral.lighter',
        px: {
          base: '3',
          sm: '3'
        },
        py: {
          base: '[0.75rem]',
          sm: '3'
        }
      })}>
          <div className={css({
          position: 'absolute',
          top: {
            base: '[5px]',
            sm: '[8px]'
          },
          left: {
            base: '[5px]',
            sm: '[8px]'
          },
          width: '[22px]',
          height: '[22px]',
          rounded: 'full',
          bg: 'blues.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'common.white',
          fontSize: '[12px]',
          fontWeight: 'bold',
          fontFamily: 'gotham',
          border: '[2px solid white]',
          boxShadow: '[0 2px 6px 0 rgba(0, 95, 185, 0.30)]',
          zIndex: '[1]'
        })}>
            {rank}
          </div>
          <div className={css({
          position: 'relative',
          flex: '1',
          rounded: 'sm',
          height: '[100%]'
        })}>
            <Image src={offer.image.source} alt={offer.displayName} fill sizes="(min-width: 640px) 150px, 100px" style={{
            objectFit: 'contain'
          }} />
          </div>
        </button>
        <div>
          <button type="button" onClick={() => handleClick(offer)} aria-label={`View details for ${offer.displayName}`} className={css({
          all: '[unset]',
          cursor: 'pointer',
          color: 'blues.main',
          fontSize: '[12px]',
          fontWeight: 'medium',
          fontFamily: 'gotham',
          textAlign: 'center',
          _hover: {
            color: 'blues.dark'
          },
          _focusVisible: {
            outline: '[2px solid {colors.blues.main}]',
            outlineOffset: '[2px]'
          }
        })}>
            Card details
          </button>
        </div>
      </article>
    </AnalyticsSkuViewed>;
}